// CSV Parser utility for LiTeCAB Desktop
// Converts CSV process data to JSON format

/**
 * Sanitize project name for use as Brightway project name
 * Converts to litecab_Project_Name format
 */
const sanitizeProjectName = (projectName) => {
  const sanitized = projectName
    .trim()
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');
  return `litecab_${sanitized}`;
};

/**
 * Parse CSV line handling quoted JSON fields
 * Properly handles commas inside JSON arrays and CSV quote escaping ("")
 */
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = i < line.length - 1 ? line[i + 1] : null;

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Double quote inside quoted field - add single quote to output
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state (opening or closing quote)
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Comma outside quotes - field separator
      result.push(current.trim());
      current = '';
    } else {
      // Regular character - add to current field
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

/**
 * Parse CSV format to project JSON
 * Format includes: process_id, next_process_id, process_name, process_type,
 * manufacturing_category, hierarchy_level, is_side_input, equipment, duration_hours,
 * temperature_c, lci_inputs, products, description
 * NOTE: input_materials removed (redundant with lci_inputs), output_materials renamed to products
 */
export const parseCSVToProject = (csvText, projectName = "Custom Project", chemistry = "Li-NMC", category = "Recycling") => {
  const lines = csvText.split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    throw new Error('CSV file must have at least a header row and one data row');
  }

  // Use parseCSVLine for header to handle quoted fields correctly
  const headers = parseCSVLine(lines[0]).map(h => h.trim());

  // Required columns
  const requiredColumns = ['process_id', 'process_name', 'manufacturing_category', 'lci_inputs'];

  // Check if required columns exist
  const missingColumns = requiredColumns.filter(col => !headers.includes(col));
  if (missingColumns.length > 0) {
    throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
  }

  const processes = {};
  const dataLines = lines.slice(1);

  dataLines.forEach((line, index) => {
    if (!line.trim()) return;

    const values = parseCSVLine(line);
    const row = {};

    headers.forEach((header, idx) => {
      row[header] = values[idx] || '';
    });

    const processId = row.process_id;
    if (!processId) {
      console.warn(`Skipping row ${index + 2}: missing process_id`);
      return;
    }

    // Parse LCI inputs and products JSON fields
    let lciInputs = [];
    let products = [];

    try {
      if (row.lci_inputs && row.lci_inputs !== '[]') {
        const parsed = JSON.parse(row.lci_inputs.replace(/^"|"$/g, '').replace(/""/g, '"'));
        // Ensure parsed result is an array
        lciInputs = Array.isArray(parsed) ? parsed : [];
        console.log(`Process ${processId}: Parsed ${lciInputs.length} LCI inputs`);
      }
    } catch (e) {
      console.error(`Process ${processId}: Failed to parse lci_inputs:`, e.message);
      console.error(`Process ${processId}: Raw lci_inputs value:`, row.lci_inputs?.substring(0, 100));
      lciInputs = [];
    }

    try {
      if (row.products && row.products !== '[]') {
        const parsed = JSON.parse(row.products.replace(/^"|"$/g, '').replace(/""/g, '"'));
        // Ensure parsed result is an array
        products = Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      console.warn(`Process ${processId}: Failed to parse products:`, e.message);
      products = [];
    }

    // Parse next_process_ids (can be semicolon-separated for multiple next processes)
    const nextProcessIds = row.next_process_id ?
      row.next_process_id.split(';').map(id => id.trim()).filter(id => id) :
      [];

    // Calculate total energy input from electricity inputs
    const energyInput = Array.isArray(lciInputs)
      ? lciInputs
          .filter(input => input && input.name && typeof input.name === 'string' && input.name.toLowerCase().includes('electricity'))
          .reduce((sum, input) => sum + (parseFloat(input.quantity) || 0), 0)
      : 0;

    processes[processId] = {
      id: processId,
      name: row.process_name || `Process ${processId}`,
      process_type: row.process_type || 'Processing',
      manufacturing_category: row.manufacturing_category || 'Recycling',  // ✅ Correct field name
      hierarchy_level: parseInt(row.hierarchy_level) || 0,
      is_side_input: row.is_side_input === 'TRUE',
      energy_input_kwh: energyInput,  // ✅ Add energy_input_kwh
      equipment: row.equipment || 'Not specified',
      parameters: {
        duration_hours: parseFloat(row.duration_hours) || 0,
        temperature_c: parseFloat(row.temperature_c) || 25,
        pressure_atm: 1
      },
      next_process_ids: nextProcessIds,
      description: row.description || '',
      products: products,  // ✅ Direct products array
      lci_inputs: lciInputs  // ✅ Simple lci_inputs without extra fields
    };
  });

  // Generate Brightway project name from project name
  const brightwayProject = sanitizeProjectName(projectName);

  return {
    project_name: projectName,
    project_info: {
      name: projectName,
      description: `${category} project imported from CSV - ${chemistry}`,
      chemistry: chemistry,
      category: category,
      battery_modules: 1,
      feedstock_lbs: 400,
      created: new Date().toISOString(),
      scaling_factor: 1.0,
      // Recycling feedstock FU is mass-based; per-kWh does not apply.
      fu_basis: 'mass',
      energy_density_wh_per_kg: null,
      brightway_project: brightwayProject,
      brightway_project_created: new Date().toISOString(),
      databases_imported: false,
      ecoinvent_version: null,
      greet_imported: false
    },
    functional_unit: {
      scale: "module",
      weight_kg: 181.44,
      battery_modules: 1,
      feedstock_type: `End-of-life ${chemistry} battery cells`
    },
    processes,
    analysis_results: { lca: {}, tea: {} }
  };
};

/**
 * Validate CSV structure
 */
export const validateCSVStructure = (csvText) => {
  try {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      return { valid: false, error: 'CSV must have at least header and one data row' };
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

    // Required columns
    const requiredColumns = ['process_id', 'process_name', 'manufacturing_category', 'lci_inputs'];

    const missingColumns = requiredColumns.filter(col =>
      !headers.some(h => h.includes(col.toLowerCase()))
    );

    if (missingColumns.length > 0) {
      return {
        valid: false,
        error: `Missing required columns: ${missingColumns.join(', ')}`,
        suggestion: 'Use the template. Required: process_id, process_name, manufacturing_category, lci_inputs (with full activity data)'
      };
    }

    const processCount = lines.length - 1;
    return {
      valid: true,
      processCount,
      message: `Valid CSV structure with ${processCount} processes`
    };

  } catch (error) {
    return {
      valid: false,
      error: `CSV parsing error: ${error.message}`
    };
  }
};

/**
 * Generate CSV template (12-process BRAWS workflow)
 * Complete battery recycling workflow with detailed process chain
 */
export const generateCSVTemplate = () => {
  return `process_id,next_process_id,process_name,process_type,manufacturing_category,hierarchy_level,is_side_input,equipment,duration_hours,temperature_c,lci_inputs,products,description
P001,P002,Battery_Refrigeration,Preparation,Recycling,0,FALSE,Refrigerator_115V_6.2A,2.5,4,"[{""name"":""Electricity US Market"",""quantity"":88.1,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],Refrigeration
P002,P003,Battery_Charging,Preparation,Recycling,0,FALSE,Arbin_RBT3_380V_45A,2.5,4,"[{""name"":""Electricity US Market"",""quantity"":89.1,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],Charging at 2C rate for 2.5h with refrigeration (89.1 kWh total: 85.5 charging + 3.6 refrigeration)
P003,P004,Cell_Discharging,Preparation,Recycling,0,FALSE,Arbin_RBT3_380V_45A,2,25,"[{""name"":""Electricity US Market"",""quantity"":17.1,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],Discharge before disassembly
P004,P005,Battery_Disassembly,Disassembly,Recycling,1,FALSE,Robotic_Disassembly_120V_15A,4,25,"[{""name"":""Electricity US Market"",""quantity"":7.2,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],Industrial scale robotic disassembly
P005,P006,Box_Evacuation,Disassembly,Recycling,1,FALSE,Vacuum_Pump_120V_15A,1,25,"[{""name"":""Electricity US Market"",""quantity"":1.8,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],Controlled atmosphere evacuation
P006,P007,Pouch_Cell_Opening,Disassembly,Recycling,1,FALSE,Robotic_Disassembly_120V_15A,24,25,"[{""name"":""Electricity US Market"",""quantity"":43.2,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],24h robotic opening of pouch cells
P007,P008; P009,Disassembled_Batteries_Dissolution,Chemical_Processing,Recycling,2,FALSE,Stainless_Steel_Reactor,3,25,"[{""name"":""Electricity US Market"",""quantity"":1.3,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""},{""name"":""Water"",""quantity"":10,""unit"":""kilogram"",""activity_name"":""water production, ultrapure"",""reference_product"":""water, ultrapure"",""location"":""RoW"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]","[{""material"":""Steel"",""quantity"":19,""unit"":""kg""},{""material"":""Copper"",""quantity"":19,""unit"":""kg""},{""material"":""Aluminum"",""quantity"":35,""unit"":""kg""},{""material"":""Separator"",""quantity"":1.71,""unit"":""kg""},{""material"":""Delithiated_Cathode"",""quantity"":54.2,""unit"":""kg""},{""material"":""Li_Solution"",""quantity"":10,""unit"":""kg""}]",95% recovery rate dissolution (10kg water becomes lithium solution)
P008,P011,Anode_Material_Separation,Chemical_Processing,Recycling,3,FALSE,Buchner_Filtration_Kit,1,25,"[{""name"":""Electricity US Market"",""quantity"":0.75,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],Separation of lithium solution from rest of materials
P009,P010,Na2CO3_Preparation,Chemical_Processing,Recycling,3,FALSE,Stainless_Steel_Reactor,3,25,"[{""name"":""Electricity US Market"",""quantity"":1.3,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""},{""name"":""Water"",""quantity"":25,""unit"":""kilogram"",""activity_name"":""water production, ultrapure"",""reference_product"":""water, ultrapure"",""location"":""RoW"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""},{""name"":""Soda Ash (Sodium Carbonate) Na2CO3"",""quantity"":5,""unit"":""kilogram"",""activity_name"":""soda production, solvay process"",""reference_product"":""soda ash, light"",""location"":""RoW"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]",[],Preparing Na2CO3 solution (25kg water + 5kg Na2CO3)
P010,P012,LiCO3_Precipitation,Chemical_Processing,Recycling,3,FALSE,Buchner_Filtration_Kit,0.5,25,[],[],Lab-scale yield scaling (2.2kg Li2CO3 from lithium solution)
P011,,Graphite_Purification,Thermal_Processing,Recycling,4,FALSE,Muffle_Furnace_1000C,3,1000,"[{""name"":""Electricity US Market"",""quantity"":28.5,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]","[{""material"":""Graphite"",""quantity"":49.4,""unit"":""kg""}]",95% recovery rate purification
P012,,Relithiated_Cathode_Processing,Thermal_Processing,Recycling,4,FALSE,Muffle_Furnace_800C,5,800,"[{""name"":""Electricity US Market"",""quantity"":47.5,""unit"":""kilowatt hour"",""activity_name"":""market group for electricity, high voltage"",""reference_product"":""electricity, high voltage"",""location"":""US"",""database"":""ecoinvent 3.11_cutoff_ecoSpold02""}]","[{""material"":""Relithiated_Cathode"",""quantity"":54.2,""unit"":""kg""}]",Relithiated cathode active material`;
};
